// @ts-nocheck
import React, { useState, useRef, useCallback, useEffect, type CSSProperties } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMixpanel } from '../hooks/useMixpanel';
import { MixpanelEvents } from '../constants/mixpanel-events';
import { showCameraSettingsAlert } from './AlertDialog';
import type { CameraModalPhoto } from '../types/camera';

const MILEAGE_PLACEHOLDERS = new Set(['vehicle_frame.svg', 'odometer-large.svg', 'odometer.svg', 'vehicle-large.svg']);

function getPlaceholderPath(placeholder: string): string {
  const folder = MILEAGE_PLACEHOLDERS.has(placeholder) ? 'mileage' : 'condition';
  return `${process.env.PUBLIC_URL}/checkin/${folder}/${placeholder}`;
}

interface CameraModalProps {
  visible: boolean;
  photo: CameraModalPhoto;
  showInstructionModal?: boolean;
  showFrame?: boolean;
  onCapture: (base64: string) => void;
  onDismiss: () => void;
}

export function CameraModal({
  visible,
  photo,
  showInstructionModal = true,
  showFrame: _showFrame = false,
  onCapture,
  onDismiss,
}: CameraModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { requestCameraPermission } = usePermissions();
  const { track } = useMixpanel();

  const [isLoading, setIsLoading] = useState(true);
  const [isTakingPicture, setIsTakingPicture] = useState(false);
  const [instructionVisible, setInstructionVisible] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (visible) {
      initCamera();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    };
  }, [visible]);

  const initCamera = async () => {
    setIsLoading(true);
    try {
      const granted = await requestCameraPermission();
      if (!granted) {
        track(MixpanelEvents.CAMERA_PERMISSION_DENIED);
        showCameraSettingsAlert();
        onDismiss();
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      setStream(mediaStream);
      setIsLoading(false);

      // Wait for next render so the video element is mounted
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      });
      if (showInstructionModal) {
        setInstructionVisible(true);
      }
    } catch {
      // If camera fails, fall back to file input
      setIsLoading(false);
    }
  };

  const takePicture = useCallback(async () => {
    if (isTakingPicture) return;
    setIsTakingPicture(true);

    try {
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          const base64 = canvas.toDataURL('image/jpeg', 0.85);
          // Cleanup stream
          if (stream) {
            stream.getTracks().forEach((t) => t.stop());
            setStream(null);
          }
          onCapture(base64);
        }
      }
    } catch (error) {
      console.warn('[CameraModal] takePicture error:', error);
    } finally {
      setIsTakingPicture(false);
    }
  }, [isTakingPicture, stream, onCapture]);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        onCapture(base64);
      };
      reader.readAsDataURL(file);
    },
    [onCapture],
  );

  if (!visible) return null;

  return (
    <div style={styles.container}>
      {/* Back button */}
      <div style={styles.topBar}>
        <button style={styles.backButton} onClick={onDismiss}>
          <ArrowLeft size={20} />
        </button>
      </div>

      {isLoading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>{t('intializing_camera')}</p>
        </div>
      ) : (
        <>
          {/* Camera preview or file input fallback */}
          <video
            ref={videoRef}
            style={styles.videoPreview}
            autoPlay
            playsInline
            muted
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* File input fallback (always available) */}
          <div style={styles.fileInputContainer}>
            <label style={styles.fileLabel}>
              {t('photo_from_gallery')}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {/* Capture button */}
          {!instructionVisible && (
            <div style={styles.bottomControls}>
              <button
                style={styles.captureButton}
                onClick={takePicture}
                disabled={isTakingPicture}
              >
                <div style={styles.captureInner} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Instruction modal */}
      {instructionVisible && (
        <div
          style={styles.instructionOverlay}
          onClick={() => setInstructionVisible(false)}
        >
          <div
            style={styles.instructionCard}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ ...styles.instructionTitle, color: theme.colors.dark }}>
              {t(`instruction.${photo.type}.title`)}
            </h3>
            {photo.placeholder ? (
              <div style={styles.placeholderContainer}>
                <img
                  src={getPlaceholderPath(photo.placeholder)}
                  alt={photo.type}
                  style={styles.placeholderImage}
                />
              </div>
            ) : null}
            <p style={{ ...styles.instructionDesc, color: theme.colors.dark }}>
              {t(`instruction.${photo.type}.description`).replace(
                /<br\s*\/?>/g,
                '\n',
              )}
            </p>
            <button
              style={{ ...styles.gotItButton, backgroundColor: theme.colors.primary }}
              onClick={() => setInstructionVisible(false)}
            >
              {t('buttons.gotit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 10,
  },
  backButton: {
    padding: 8,
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: 28,
    cursor: 'pointer',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #555',
    borderTop: '3px solid #3880ff',
    borderRadius: '50%',
    animation: 'ownli-spin 1s linear infinite',
  },
  loadingText: {
    color: '#fff',
    marginTop: 12,
    fontSize: 14,
  },
  videoPreview: {
    flex: 1,
    objectFit: 'cover',
    width: '100%',
  },
  fileInputContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
  },
  fileLabel: {
    padding: '8px 16px',
    backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 12,
    cursor: 'pointer',
  },
  bottomControls: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  captureButton: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(255,255,255,0.3)',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  captureInner: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: '#fff',
  },
  instructionOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 11000,
  },
  instructionCard: {
    width: '80%',
    maxWidth: 400,
    borderRadius: 12,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.45)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  instructionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  placeholderContainer: {
    width: 180,
    height: 140,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    overflow: 'hidden',
  },
  placeholderImage: {
    maxWidth: '90%',
    maxHeight: '90%',
    objectFit: 'contain',
  },
  instructionDesc: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: '20px',
    whiteSpace: 'pre-line',
  },
  gotItButton: {
    padding: '10px 32px',
    borderRadius: 4,
    border: 'none',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
    cursor: 'pointer',
  },
};
